<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { decodeBeadRoadCode } from '../domain/beadRoad.js';
import { drawRoadWatermark, loadRoadWatermark } from '../domain/roadWatermark.js';
import { drawGlassBead, drawGlassDot, drawRoadBoard, drawRoadGridLine, ROAD_THEMES } from '../domain/roadVisual.js';
import { runtimeLogger } from '../services/runtimeLogger.js';
import { latestChangedVisibleRoadIndex, resolveRoadModel, shouldFlashRoadResult } from '../domain/roadFlash.js';

const props = defineProps({
  analysis: { type: Object, default: () => ({}) },
  mode: { type: String, default: 'main' },
  tableKey: { type: [Number, String], required: true },
  flashSeq: { type: Number, default: 0 },
  t: { type: Function, required: true }
});
const canvas = ref(null);
let blinkHidden = false;
let blinkIndex = -1;
let blinkTimer = 0;

function source() {
  return String(resolveRoadModel(props.analysis, props.mode).LuZhu || '');
}

function resultSource() {
  return String(resolveRoadModel(props.analysis, 'main').LuZhu || '');
}

function drawBeadValue(ctx, value, index) {
  const bead = decodeBeadRoadCode(value);
  if (!bead) return;
  const zeroBased = index - 1;
  const col = zeroBased % 20;
  const row = Math.floor(zeroBased / 20);
  if (col < 0 || col >= 20 || row < 0 || row >= 6) return;
  const color = bead.winner === 'banker' ? 'red' : bead.winner === 'player' ? 'blue' : 'green';
  const label = bead.winner === 'banker'
    ? props.t('labelCompact.banker', 'B')
    : bead.winner === 'player'
      ? props.t('labelCompact.player', 'P')
      : props.t('labelCompact.tie', 'T');
  const cx = col * 54 + 27;
  const cy = row * 54 + 27;
  drawGlassBead(ctx, cx, cy, 23.5, ROAD_THEMES[color], label);
  if (bead.bankerPair) drawGlassDot(ctx, cx - 18, cy - 18, 6.5, ROAD_THEMES.red);
  if (bead.playerPair) drawGlassDot(ctx, cx + 17.5, cy + 17.5, 6.5, ROAD_THEMES.blue);
}

function draw() {
  const node = canvas.value;
  if (!node) return;
  const ctx = node.getContext('2d');
  const width = 1082;
  const height = 328;
  node.width = width;
  node.height = height;
  drawRoadBoard(ctx, width, height);
  for (let row = 0; row <= 6; row += 1) drawRoadGridLine(ctx, 0, row * 54, width, row * 54, true);
  for (let col = 0; col <= 20; col += 1) drawRoadGridLine(ctx, col * 54, 0, col * 54, 324, true);
  drawRoadWatermark(ctx, width, height, { widthRatio: .43, opacity: .105 });

  const mainSource = resultSource();
  const previewSource = source();
  const values = mainSource.split('@');
  for (let row = 0; row < 6; row += 1) {
    for (let col = 0; col < 20; col += 1) {
      const index = col + row * 20 + 1;
      if (blinkHidden && index === blinkIndex) continue;
      drawBeadValue(ctx, values[index], index);
    }
  }
  if (props.mode !== 'main') {
    const previewIndex = latestChangedVisibleRoadIndex(mainSource, previewSource, { sourceColumns: 20, visibleColumns: 20, visibleRows: 6 });
    if (previewIndex >= 1) drawBeadValue(ctx, previewSource.split('@')[previewIndex], previewIndex);
  }
}

function flashNewest() {
  window.clearInterval(blinkTimer);
  let transitions = 0;
  blinkHidden = false;
  blinkTimer = window.setInterval(() => {
    transitions += 1;
    blinkHidden = !blinkHidden;
    draw();
    if (transitions >= 6) {
      window.clearInterval(blinkTimer);
      blinkTimer = 0;
      blinkHidden = false;
      draw();
    }
  }, 240);
}

onMounted(() => {
  draw();
  loadRoadWatermark().then(draw).catch((error) => runtimeLogger.error('[wanli-road] mobile bead watermark failed', error));
});
onBeforeUnmount(() => window.clearInterval(blinkTimer));
watch(() => [props.tableKey, props.flashSeq, resultSource()], (next, previous = []) => {
  const [nextTableKey, nextSeq, nextSource] = next;
  const [previousTableKey, previousSeq, previousSource] = previous;
  if (!shouldFlashRoadResult({ previousTableKey, nextTableKey, previousSeq, nextSeq })) {
    if (String(previousTableKey ?? '') !== String(nextTableKey ?? '')) {
      window.clearInterval(blinkTimer);
      blinkTimer = 0;
      blinkHidden = false;
      blinkIndex = -1;
      nextTick(draw);
    }
    return;
  }
  blinkIndex = latestChangedVisibleRoadIndex(previousSource, nextSource, { sourceColumns: 20, visibleColumns: 20, visibleRows: 6 });
  if (blinkIndex >= 1) flashNewest();
});
watch(() => [props.analysis, props.mode, props.t('labelCompact.banker'), props.t('labelCompact.player'), props.t('labelCompact.tie')], () => nextTick(draw), { deep: true });
</script>

<template><canvas ref="canvas" class="game-road-full mobile-bead-road" :data-road-mode="mode" width="1082" height="328" /></template>
